/**
 * BootSplash — an in-app splash overlay that bridges the gap between the native
 * launch splash and the first app screen, so iOS shows the SAME shield for the
 * whole launch instead of a white screen + spinner.
 *
 * Why: Android's OS splash (windowSplashScreen) holds the shield through JS
 * boot + auth bootstrap, so the user only ever sees the shield. iOS's launch
 * storyboard disappears at the first rendered frame — it can't be "held" — so
 * once React paints, the auth-loading spinner (PageFallback) shows through.
 * This overlay reproduces the shield in-app and stays up until the app is ready
 * (auth bootstrap done), matching Android. Native-only: web never had the gap.
 */
import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useAuth } from "@/contexts/AuthContext";

const FADE_MS = 300;
// Safety cap so the overlay can never get stuck if auth bootstrap hangs.
const MAX_MS = 6000;

export function BootSplash() {
  const { loading } = useAuth();
  // Web starts already gone (no native launch splash to bridge), so it never
  // even renders there.
  const [gone, setGone] = useState(() => !Capacitor.isNativePlatform());
  const [fading, setFading] = useState(false);

  // Start fading once auth bootstrap finishes (app ready) — or after the cap.
  useEffect(() => {
    if (gone) return;
    const cap = setTimeout(() => setFading(true), MAX_MS);
    if (!loading) setFading(true);
    return () => clearTimeout(cap);
  }, [loading, gone]);

  // Unmount after the fade-out transition completes.
  useEffect(() => {
    if (!fading) return;
    const t = setTimeout(() => setGone(true), FADE_MS);
    return () => clearTimeout(t);
  }, [fading]);

  if (gone) return null;
  return (
    <div
      aria-hidden
      className="fixed inset-0 z-[9999] grid place-items-center bg-white pointer-events-none"
      style={{ transition: `opacity ${FADE_MS}ms ease`, opacity: fading ? 0 : 1 }}
    >
      <img src="/splash-shield.svg" alt="" className="h-40 w-40 select-none" draggable={false} />
    </div>
  );
}
